import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ClipboardIcon,
  CheckIcon,
  ArrowPathIcon,
  FunnelIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function JiraTicketsTable() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/JiraTickets/tickets`
      );
      if (!resp.ok) throw new Error("Failed to load tickets");
      const data = await resp.json();

      // Supporte les deux formats : {items:[...]} OU tableau direct
      const items = Array.isArray(data) ? data : data.items || [];
      setTickets(items);
    } catch (e) {
      console.error(e);
      setError(t("jira.errors.loadTickets"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (val) => {
    navigator.clipboard.writeText(val || "");
    setCopiedKey(val);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const formatDate = (val) => {
    if (!val) return "—";
    try {
      // accepte string ou Date
      const d = typeof val === "string" ? new Date(val) : val;
      return new Intl.DateTimeFormat(i18n.language || "fr-CA", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }).format(d);
    } catch {
      return val;
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: "all", label: t("jira.filters.all") },
      { value: "À planifier", label: t("jira.status.toPlan") },
      { value: "En cours", label: t("jira.status.inProgress") },
      { value: "En attente", label: t("jira.status.blocked") },
      { value: "Terminé", label: t("jira.status.done") },
      { value: "Déployé", label: t("jira.status.deployed") },
    ],
    [t]
  );

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return tickets
      .filter((it) =>
        statusFilter === "all" ? true : (it.status || "") === statusFilter
      )
      .filter((it) => {
        if (!q) return true;
        const hay = `${it.key ?? ""} ${it.title ?? ""} ${
          it.description ?? ""
        } ${it.status ?? ""}`
          .toLowerCase()
          .normalize("NFD")
          .replace(/\p{Diacritic}/gu, "");
        const needle = q.normalize("NFD").replace(/\p{Diacritic}/gu, "");
        return hay.includes(needle);
      });
  }, [tickets, search, statusFilter]);

  const badgeClass = (status) => {
    // Cohérent avec tes styles DaisyUI/Tailwind
    switch (status) {
      case "À planifier":
        return "badge bg-base-200 text-base-content border border-base-300";
      case "En cours":
        return "badge bg-blue-100 text-blue-700 border border-blue-200";
      case "En attente":
        return "badge bg-amber-100 text-amber-800 border border-amber-200";
      case "Terminé":
        return "badge bg-emerald-100 text-emerald-800 border border-emerald-200";
      case "Déployé":
        return "badge bg-purple-100 text-purple-800 border border-purple-200";
      default:
        return "badge bg-base-200 text-base-content border border-base-300";
    }
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">{t("jira.title")}</h1>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="join w-full md:w-80">
            <input
              type="text"
              className="input input-bordered join-item w-full"
              placeholder={t("jira.search.placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              className="btn join-item"
              onClick={loadTickets}
              title={t("jira.actions.refresh")}
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-base-content/70" />
            <select
              className="select select-bordered select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-base-content/80">
          <span className="loading loading-spinner loading-md" />
          <span>{t("jira.loading")}</span>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <InformationCircleIcon className="w-5 h-5" />
          <span>{error}</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-500">{t("jira.empty")}</div>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-base-100 shadow-sm">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>{t("jira.columns.key")}</th>
                <th>{t("jira.columns.title")}</th>
                <th className="hidden md:table-cell">
                  {t("jira.columns.description")}
                </th>
                <th>{t("jira.columns.status")}</th>
                <th>{t("jira.columns.dueDate")}</th>
                <th>{t("jira.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.key}>
                  <td className="font-mono">{it.key}</td>
                  <td className="max-w-[340px]">
                    <div className="line-clamp-2">{it.title || "—"}</div>
                  </td>
                  <td className="hidden md:table-cell max-w-[520px]">
                    <div className="text-sm text-base-content/80 line-clamp-2">
                      {it.description || "—"}
                    </div>
                  </td>
                  <td>
                    <span className={badgeClass(it.status)}>
                      {translateStatus(it.status, t)}
                    </span>
                  </td>
                  <td>{formatDate(it.dueDate)}</td>
                  <td className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopy(it.key)}
                      className="btn btn-sm btn-ghost"
                      title={t("jira.actions.copyKey")}
                    >
                      {copiedKey === it.key ? (
                        <CheckIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <ClipboardIcon className="w-5 h-5 text-gray-700" />
                      )}
                    </button>
                    {/* Slot future: ouvrir un modal détail si besoin */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Traduction “défensive” du statut (si jamais l’API retourne anglais/fr selon contexte) */
function translateStatus(status, t) {
  switch ((status || "").toLowerCase()) {
    case "à planifier":
    case "a planifier":
    case "à faire":
    case "to do":
    case "todo":
    case "backlog":
    case "selected for development":
      return t("jira.status.toPlan");
    case "en cours":
    case "in progress":
      return t("jira.status.inProgress");
    case "en attente":
    case "blocked":
    case "waiting":
    case "on hold":
      return t("jira.status.blocked");
    case "terminé":
    case "done":
    case "resolved":
      return t("jira.status.done");
    case "déployé":
    case "deployed":
      return t("jira.status.deployed");
    default:
      return status || t("jira.status.toPlan");
  }
}
